/**
 * ===================================================
 * Module: frontend.src.utils.fingerprint.ts
 * Description: Robust browser device fingerprint generator
 *              Uses canvas, WebGL, screen, timezone, and UA signals
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

/**
 * Generate a robust device fingerprint using multiple browser signals.
 * This creates a hash that is consistent for the same browser/device
 * but differs across different devices.
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const signals: string[] = [];

  // 1. User-Agent
  signals.push(navigator.userAgent || "");

  // 2. Language and timezone
  signals.push(navigator.language || "");
  signals.push(Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  signals.push(String(new Date().getTimezoneOffset()));

  // 3. Screen properties
  signals.push(`${window.screen.width}x${window.screen.height}`);
  signals.push(`${window.screen.colorDepth}`);
  signals.push(`${window.devicePixelRatio || 1}`);

  // 4. Platform
  signals.push(navigator.platform || "");

  // 5. Hardware concurrency
  signals.push(String(navigator.hardwareConcurrency || 0));

  // 6. Canvas fingerprint
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = "14px 'Arial'";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("KTS Arcana FP", 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText("KTS Arcana FP", 4, 17);
      signals.push(canvas.toDataURL());
    }
  } catch {
    signals.push("canvas-unavailable");
  }

  // 7. WebGL renderer
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        signals.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "");
        signals.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "");
      }
    }
  } catch {
    signals.push("webgl-unavailable");
  }

  // 8. Available fonts (basic check via canvas)
  try {
    const testFonts = ["monospace", "serif", "sans-serif"];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const fontResults = testFonts.map((font) => {
        ctx.font = `72px ${font}`;
        return ctx.measureText("mmmmmmmmlli").width;
      });
      signals.push(fontResults.join(","));
    }
  } catch {
    signals.push("fonts-unavailable");
  }

  // Hash all signals together
  const raw = signals.join("|||");
  return await sha256(raw);
}

/**
 * SHA-256 hash using Web Crypto API
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Get stored trust token from localStorage
 */
export function getDeviceTrustToken(): string | null {
  return localStorage.getItem("device_trust_token");
}

/**
 * Store trust token in localStorage
 */
export function setDeviceTrustToken(token: string): void {
  localStorage.setItem("device_trust_token", token);
}

/**
 * Remove trust token from localStorage
 */
export function clearDeviceTrustToken(): void {
  localStorage.removeItem("device_trust_token");
}
